import React, { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Clock, MapPin, TrendingDown, Wind } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const AlertsPanel = () => {
  const [expandedAlert, setExpandedAlert] = useState(null);

  const alerts = [
    {
      id: 1,
      severity: 'warning',
      title: 'Weather Watch',
      desc: 'Barometric pressure falling 2 hPa/hr.',
      timestamp: '2025-11-12 11:45 UTC',
      location: 'Lat -23.745°, Lon -134.120°',
      details: {
        currentPressure: '1012 hPa',
        forecast: 'Pressure expected to drop to 998 hPa in next 6 hours',
        windSpeed: '15 kt, increasing to 25-30 kt',
        recommendation: 'Monitor weather closely. Consider altering course if pressure drops below 1000 hPa.',
      },
      actions: [
        { label: 'View Forecast', variant: 'default', icon: Wind },
        { label: 'Update Route', variant: 'secondary', icon: MapPin },
        { label: 'Dismiss', variant: 'ghost', icon: null },
      ],
    },
    {
      id: 2,
      severity: 'info',
      title: 'Sonar Sync',
      desc: 'Ping jitter corrected to 2 Hz stable.',
      timestamp: '2025-11-12 10:22 UTC',
      location: 'System',
      details: {
        previousJitter: '± 5 Hz',
        currentJitter: '± 0.5 Hz',
        signalQuality: '98% stable',
        recommendation: 'No action required. System automatically recalibrated.',
      },
      actions: [
        { label: 'View Sonar Log', variant: 'secondary', icon: null },
        { label: 'Acknowledge', variant: 'ghost', icon: null },
      ],
    },
    {
      id: 3,
      severity: 'info',
      title: 'System Update',
      desc: 'Navigation software updated to v2.4.1.',
      timestamp: '2025-11-12 08:15 UTC',
      location: 'System',
      details: {
        version: 'v2.4.1',
        changes: [
          'Improved GPS accuracy by 15%',
          'Enhanced route optimization algorithm',
          'Bug fixes for depth sensor integration',
        ],
        recommendation: 'Review changelog for detailed improvements.',
      },
      actions: [
        { label: 'View Changelog', variant: 'secondary', icon: null },
        { label: 'Acknowledge', variant: 'ghost', icon: null },
      ],
    },
  ];

  const getSeverityConfig = (severity) => {
    const configs = {
      warning: {
        borderColor: 'border-l-amber-400',
        iconColor: 'text-amber-400',
        bgColor: 'bg-amber-500/5',
        badgeVariant: 'default',
        badgeClass: 'bg-amber-500/20 text-amber-300 border-amber-400/30',
      },
      error: {
        borderColor: 'border-l-red-400',
        iconColor: 'text-red-400',
        bgColor: 'bg-red-500/5',
        badgeVariant: 'destructive',
        badgeClass: 'bg-red-500/20 text-red-300 border-red-400/30',
      },
      info: {
        borderColor: 'border-l-sky-400',
        iconColor: 'text-sky-400',
        bgColor: 'bg-sky-500/5',
        badgeVariant: 'outline',
        badgeClass: 'bg-sky-500/20 text-sky-300 border-sky-400/30',
      },
    };
    return configs[severity] || configs.info;
  };

  const toggleExpand = (alertId) => {
    setExpandedAlert(expandedAlert === alertId ? null : alertId);
  };

  return (
    <Card className="col-span-12 xl:col-span-5 animate-fade-in" data-testid="alerts-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Status & Alerts</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {alerts.map((alert) => {
          const config = getSeverityConfig(alert.severity);
          const isExpanded = expandedAlert === alert.id;
          
          return (
            <div
              key={alert.id}
              className={`rounded-md border border-border/40 border-l-4 ${config.borderColor} ${config.bgColor} transition-all`}
              data-testid={`alert-${alert.id}`}
            >
              {/* Alert Header */}
              <div 
                className="p-4 cursor-pointer hover:bg-secondary/10 transition-colors"
                onClick={() => toggleExpand(alert.id)}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${config.iconColor}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-sm" data-testid={`alert-title-${alert.id}`}>{alert.title}</h4>
                      <Badge variant="outline" className={`text-xs ${config.badgeClass}`}>
                        {alert.severity}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground" data-testid={`alert-desc-${alert.id}`}>{alert.desc}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground/70">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>{alert.timestamp}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        <span>{alert.location}</span>
                      </div>
                    </div>
                  </div>
                  <div className={`text-muted-foreground transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                    <ChevronDown className="h-4 w-4" />
                  </div>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-2 border-t border-border/20 animate-in slide-in-from-top-2" data-testid={`alert-details-${alert.id}`}>
                  <div className="ml-7 space-y-3">
                    {/* Details Section */}
                    <div className="space-y-2">
                      <h5 className="text-xs font-semibold text-muted-foreground uppercase">Details</h5>
                      <div className="space-y-1.5 text-xs">
                        {Object.entries(alert.details).map(([key, value]) => {
                          if (key === 'changes' && Array.isArray(value)) {
                            return (
                              <div key={key}>
                                <span className="text-muted-foreground capitalize">{key}:</span>
                                <ul className="ml-4 mt-1 space-y-1">
                                  {value.map((change, idx) => (
                                    <li key={idx} className="text-foreground list-disc">{change}</li>
                                  ))}
                                </ul>
                              </div>
                            );
                          }
                          return (
                            <div key={key} className="flex justify-between">
                              <span className="text-muted-foreground capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                              <span className="text-foreground font-medium">{value}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Recommendation Box */}
                    {alert.details.recommendation && (
                      <div className="p-3 rounded-md bg-secondary/30 border border-border/40">
                        <div className="flex items-start gap-2">
                          <TrendingDown className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                          <div>
                            <h5 className="text-xs font-semibold mb-1">Recommendation</h5>
                            <p className="text-xs text-muted-foreground">{alert.details.recommendation}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2">
                      {alert.actions.map((action, idx) => {
                        const Icon = action.icon;
                        return (
                          <Button
                            key={idx}
                            variant={action.variant}
                            size="sm"
                            className="h-8 text-xs"
                            data-testid={`alert-action-${alert.id}-${idx}`}
                          >
                            {Icon && <Icon className="h-3 w-3 mr-1.5" />}
                            {action.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default AlertsPanel;
